import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  FileDownload as TemplateIcon,
  Description as CsvIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';

function NotificationImport({ onImport, onClose, open }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef(null);

  const templateData = [
    {
      message: 'Welcome to our platform!',
      type: 'general',
      priority: 'normal',
      channel: 'in-app',
      related: 'User Registration',
      time: '',
      read: 'false',
    },
    {
      message: 'Your account has been verified successfully.',
      type: 'success',
      priority: 'normal',
      channel: 'email',
      related: 'Account Verification',
      time: '',
      read: 'false',
    },
    {
      message: 'System maintenance scheduled for tomorrow.',
      type: 'warning',
      priority: 'high',
      channel: 'in-app',
      related: 'System Maintenance',
      time: '2024-01-15T10:00:00Z',
      read: 'false',
    },
    {
      message: 'Payment failed. Please update your payment method.',
      type: 'error',
      priority: 'urgent',
      channel: 'email',
      related: 'Payment Issue',
      time: '',
      read: 'false',
    },
  ];

  const downloadTemplate = () => {
    const csv = Papa.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'notification_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setErrors([]);
      setResults(null);
    } else {
      setErrors(['Please select a valid CSV file']);
    }
  };

  const validateRow = (row, index) => {
    const rowErrors = [];

    if (!row.message || row.message.trim() === '') {
      rowErrors.push('Message is required');
    }

    if (row.message && row.message.length > 500) {
      rowErrors.push('Message must be less than 500 characters');
    }

    const validTypes = ['general', 'info', 'warning', 'error', 'success'];
    if (row.type && !validTypes.includes(row.type.toLowerCase())) {
      rowErrors.push('Invalid type. Must be one of: general, info, warning, error, success');
    }

    const validPriorities = ['low', 'normal', 'high', 'urgent'];
    if (row.priority && !validPriorities.includes(row.priority.toLowerCase())) {
      rowErrors.push('Invalid priority. Must be one of: low, normal, high, urgent');
    }

    const validChannels = ['in-app', 'email', 'sms', 'push'];
    if (row.channel && !validChannels.includes(row.channel.toLowerCase())) {
      rowErrors.push('Invalid channel. Must be one of: in-app, email, sms, push');
    }

    if (row.time && row.time.trim() !== '') {
      const date = new Date(row.time);
      if (isNaN(date.getTime())) {
        rowErrors.push('Invalid date format. Use ISO format (YYYY-MM-DDTHH:mm:ssZ)');
      }
    }

    if (row.read && !['true', 'false'].includes(row.read.toLowerCase())) {
      rowErrors.push('Read status must be true or false');
    }

    return rowErrors;
  };

  const processFile = async () => {
    if (!file) return;

    setUploading(true);
    setProgress(0);
    setErrors([]);
    setResults(null);

    try {
      const text = await file.text();
      const result = Papa.parse(text, { header: true, skipEmptyLines: true });

      if (result.errors.length > 0) {
        setErrors([`CSV parsing errors: ${result.errors.map((e) => e.message).join(', ')}`]);
        setUploading(false);
        return;
      }

      const validRows = [];
      const invalidRows = [];
      let processedCount = 0;

      for (let i = 0; i < result.data.length; i++) {
        const row = result.data[i];
        const rowErrors = validateRow(row, i + 2); // +2 for header and 1-based index

        if (rowErrors.length === 0) {
          // Transform row data
          const transformedRow = {
            message: row.message.trim(),
            type: (row.type || 'general').toLowerCase(),
            priority: (row.priority || 'normal').toLowerCase(),
            channel: (row.channel || 'in-app').toLowerCase(),
            related: row.related || '',
            time: row.time && row.time.trim() !== '' ? new Date(row.time).toISOString() : null,
            read: row.read ? row.read.toLowerCase() === 'true' : false,
          };
          validRows.push(transformedRow);
        } else {
          invalidRows.push({
            row: i + 2,
            data: row,
            errors: rowErrors,
          });
        }

        processedCount++;
        setProgress((processedCount / result.data.length) * 100);
      }

      if (invalidRows.length > 0) {
        setErrors(invalidRows.map((row) => `Row ${row.row}: ${row.errors.join(', ')}`));
        setUploading(false);
        return;
      }

      if (validRows.length === 0) {
        setErrors(['No valid rows found in the CSV file']);
        setUploading(false);
        return;
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setResults({
        total: validRows.length,
        success: validRows.length,
        failed: 0,
        data: validRows,
      });

      if (onImport) {
        onImport(validRows);
      }
    } catch (error) {
      setErrors([`Error processing file: ${error.message}`]);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploading(false);
    setProgress(0);
    setResults(null);
    setErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Notifications</Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Template Download */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <TemplateIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Download Template
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Download the CSV template to see the required format and sample data.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
              >
                Download Template
              </Button>
            </Stack>
          </Paper>

          {/* File Upload */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Upload CSV File
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <Stack spacing={2}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                fullWidth
                sx={{ py: 2 }}
              >
                {file ? `Selected: ${file.name}` : 'Choose CSV File'}
              </Button>

              {file && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CsvIcon color="primary" />
                  <Typography variant="body2">
                    {file.name}
                    {' '}
                    (
                    {(file.size / 1024).toFixed(1)}
                    {' '}
                    KB)
                  </Typography>
                </Stack>
              )}

              {file && !uploading && (
                <Button
                  variant="contained"
                  onClick={processFile}
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Process File
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Progress */}
          {uploading && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Processing File...
              </Typography>
              <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}
                % complete
              </Typography>
            </Paper>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle2" gutterBottom>
                Import Errors:
              </Typography>
              <List dense>
                {errors.map((error, index) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ErrorIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText primary={error} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {/* Results */}
          {results && (
            <Alert severity="success">
              <Typography variant="subtitle2" gutterBottom>
                Import Results:
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <SuccessIcon fontSize="small" />
                  <Typography variant="body2">
                    {results.success}
                    {' '}
                    notifications imported successfully
                  </Typography>
                </Stack>
                {results.failed > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ErrorIcon fontSize="small" color="error" />
                    <Typography variant="body2" color="error">
                      {results.failed}
                      {' '}
                      failed
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Alert>
          )}

          {/* Instructions */}
          <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <InfoIcon color="info" />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Import Instructions:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    <li>Message is required and must be less than 500 characters</li>
                    <li>Type must be: general, info, warning, error, or success</li>
                    <li>Priority must be: low, normal, high, or urgent</li>
                    <li>Channel must be: in-app, email, sms, or push</li>
                    <li>Time should be in ISO format (YYYY-MM-DDTHH:mm:ssZ) or leave empty</li>
                    <li>Read status must be true or false</li>
                  </ul>
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NotificationImport;
